import * as React from 'react';
import {
  useServiceAuth,
  useActiveAccountId,
  useActiveProjectId,
  useActiveProjectCohortId,
} from '../hooks';
import FhirService from '../services/FhirService';
import FileService from '../services/FileService';

const fhirService = new FhirService('DocumentReference');
const fileService = new FileService();

/**
 * Extracts the file ID from the attachment URL of a DocumentReference.
 * @param {fhir.DocumentReference | undefined} documentReference - The DocumentReference object.
 * @param {number} [attachmentIndex=0] - The index of the attachment to use (default is 0).
 * @returns {string | undefined} The file ID extracted from the attachment URL.
 */
const getAttachmentFileId = (
  documentReference: fhir.DocumentReference | undefined,
  attachmentIndex: number = 0,
): string | undefined => {
  const attachment = documentReference?.content?.[attachmentIndex].attachment;

  return attachment?.url?.split('/').pop();
};

/**
 * Custom hook for managing DocumentReference retrieval and attachment fetching.
 * @returns {Object} An object containing methods for document reference and attachment retrieval.
 */
export const useDocumentReference = () => {
  const serviceAuth = useServiceAuth();
  const account = useActiveAccountId();
  const project = useActiveProjectId();
  const cohort = useActiveProjectCohortId();
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [documentReference, setDocumentReference] =
    React.useState<fhir.DocumentReference>();

  const retrieveDocumentReference = async (id: string): Promise<any> => {
    if (id || id !== documentReference?.id) {
      const response = await fhirService.getById(serviceAuth)(account, id);
      if (mountedRef.current) {
        setDocumentReference(response.data);
      }
      return response.data;
    } else {
      return documentReference;
    }
  };

  /**
   * Retrieves the attachment associated with a DocumentReference.
   * @param {fhir.DocumentReference} docRef - The DocumentReference object containing the attachment.
   * @returns {Promise<any>} A promise that resolves to the attachment data.
   */
  const retrieveAttachment = async (
    docRef: fhir.DocumentReference,
  ): Promise<any> => {
    const fileId = getAttachmentFileId(docRef);
    const subjectId = docRef.subject?.reference?.split('/').pop();

    if (!fileId) {
      // TODO handle fileId not found
      console.warn('fileId not found');
      return;
    }

    const result = await fileService.fetchOpenLink(serviceAuth)(fileId, {
      project,
      cohort,
      subjectId,
    });

    return result.data;
  };

  return {
    retrieveDocumentReference,
    retrieveAttachment,
  };
};
